import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { webPortalTarget } from '../../platforms/web/desktop/webPortalTarget';
import { AiSnapItemEditorSheet } from './AiSnapItemEditorSheet';
import type { AiSnapAnalysis } from './useAiSnapModel';
import { useAiSnapModel } from './useAiSnapModel';

const fmt = (v: any, digits = 0) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const p = Math.pow(10, digits);
  return String(Math.round(n * p) / p);
};

type Verdict = { text: string; tone: 'good' | 'ok' | 'warn' | 'bad' };
type MacroTone = 'good' | 'ok' | 'warn';

const DAY_TARGET = { calories: 2200, protein: 60 };

const getVerdict = (totals: Record<string, any>, suggestions: any | undefined): Verdict => {
  const v = suggestions?.verdict;
  if (v && typeof v === 'object') {
    const label = String(v.label || '').trim();
    const tone = String(v.tone || '').trim();
    if (label && (tone === 'good' || tone === 'ok' || tone === 'warn' || tone === 'bad')) {
      return { text: label, tone } as Verdict;
    }
  }

  const kcal = Number(totals.calories) || 0;
  const sugar = Number(totals.sugar) || 0;
  const sodium = Number(totals.sodium) || 0;
  const alcohol = Number(totals.alcohol) || 0;
  const protein = Number(totals.protein) || 0;

  const sugList: any[] = Array.isArray(suggestions?.suggestions) ? suggestions.suggestions : [];
  const warningCount = sugList.filter(s => (s?.type || '') === 'warning').length;
  const allText = [
    String(suggestions?.overall_rating || ''),
    String(suggestions?.overall_note || ''),
    ...sugList.map(s => `${s?.title || ''} ${s?.detail || ''}`),
  ].join(' ').toLowerCase();

  // If AI suggestions already determined caution, trust it first.
  // This incorporates user's health report/abnormals because backend prompt includes health context.
  if (allText.includes('不建议') || allText.includes('避免') || allText.includes('风险较大')) {
    return { text: '建议跳过', tone: 'bad' };
  }
  if (warningCount >= 2) return { text: '谨慎记录', tone: 'warn' };
  if (warningCount >= 1) {
    // If warning explicitly mentions chronic/abnormal indicators, escalate.
    const critical = ['血压', '高血压', '血糖', '糖尿病', '尿酸', '脂肪肝', '肾', '痛风', '高脂', '胆固醇', '心血管'];
    if (critical.some(k => allText.includes(k))) return { text: '谨慎记录', tone: 'warn' };
  }

  // Numeric risk scoring (kcal + sugar + sodium + alcohol)
  let risk = 0;
  if (kcal >= 1000) risk += 3;
  else if (kcal >= 800) risk += 2;
  else if (kcal >= 650) risk += 1;

  if (sugar >= 50) risk += 3;
  else if (sugar >= 35) risk += 2;
  else if (sugar >= 25) risk += 1;

  if (sodium >= 2000) risk += 3;
  else if (sodium >= 1500) risk += 2;
  else if (sodium >= 1000) risk += 1;

  if (alcohol >= 20) risk += 3;
  else if (alcohol >= 10) risk += 2;
  else if (alcohol > 0) risk += 1;

  // Warnings raise risk
  risk += Math.min(3, warningCount);

  if (risk >= 6) return { text: '建议跳过', tone: 'bad' };
  if (risk >= 3) return { text: alcohol > 0 ? '小酌即可' : '谨慎记录', tone: 'warn' };

  // Green light: decent protein, not too sugary/salty
  if (kcal <= 650 && sugar <= 25 && sodium <= 900 && protein >= 20) return { text: '放心记录', tone: 'good' };
  if (kcal <= 700 && sugar <= 30 && sodium <= 1100) return { text: '可记录', tone: 'good' };
  return { text: '适量记录', tone: 'ok' };
};

const getMacroStatus = (key: string, value: any): { text: string; tone: MacroTone } => {
  const n = Number(value) || 0;
  if (key === 'calories') {
    if (n >= 750) return { text: '偏高', tone: 'warn' };
    if (n >= 450) return { text: '中等', tone: 'ok' };
    return { text: '轻负担', tone: 'good' };
  }
  if (key === 'protein') {
    if (n >= 30) return { text: '很好', tone: 'good' };
    if (n >= 15) return { text: '正常', tone: 'ok' };
    return { text: '偏低', tone: 'warn' };
  }
  if (key === 'carbs') {
    if (n >= 80) return { text: '偏高', tone: 'warn' };
    if (n >= 35) return { text: '正常', tone: 'ok' };
    return { text: '较低', tone: 'good' };
  }
  if (key === 'fat') {
    if (n >= 30) return { text: '偏高', tone: 'warn' };
    if (n >= 12) return { text: '正常', tone: 'ok' };
    return { text: '较低', tone: 'good' };
  }
  if (key === 'sugar') {
    if (n >= 25) return { text: '偏高', tone: 'warn' };
    if (n >= 10) return { text: '注意', tone: 'ok' };
    return { text: '较低', tone: 'good' };
  }
  if (key === 'sodium') {
    if (n >= 900) return { text: '偏高', tone: 'warn' };
    if (n >= 450) return { text: '注意', tone: 'ok' };
    return { text: '正常', tone: 'good' };
  }
  return { text: '参考', tone: 'ok' };
};

const buildInsight = (totals: Record<string, any>, category?: string, selectedCount = 0) => {
  const calories = Number(totals.calories) || 0;
  const protein = Number(totals.protein) || 0;
  const carbs = Number(totals.carbs) || 0;
  const fat = Number(totals.fat) || 0;
  const sodium = Number(totals.sodium) || 0;
  const sugar = Number(totals.sugar) || 0;
  const alcohol = Number(totals.alcohol) || 0;
  const calPct = Math.round((calories / DAY_TARGET.calories) * 100);

  let title = '这餐比较均衡';
  let copy = `约 ${fmt(calories, 0)} kcal，占日目标约 ${calPct}%。核对菜品和份量后就可以记录。`;
  let tone: Verdict['tone'] = 'good';

  if (category === 'alcohol' || alcohol > 0) {
    title = '这次饮品需要适量';
    copy = `约 ${fmt(calories, 0)} kcal，含酒精约 ${fmt(alcohol, 1)}g。建议补水，后续少叠加高油高盐食物。`;
    tone = 'warn';
  } else if (calories >= 750 || calPct >= 35) {
    title = '这餐偏高热量';
    copy = `约 ${fmt(calories, 0)} kcal，占日目标约 ${calPct}%。下一餐建议减少主食，多补蔬菜和水。`;
    tone = 'warn';
  } else if (protein >= 30 && carbs <= 80 && fat <= 30) {
    title = '这餐蛋白表现不错';
    copy = `约 ${fmt(calories, 0)} kcal，蛋白 ${fmt(protein, 1)}g。整体负担可控，继续保持这个节奏。`;
    tone = 'good';
  } else if (protein < 15 && category !== 'beverage') {
    title = '这餐蛋白偏少';
    copy = `约 ${fmt(calories, 0)} kcal，但蛋白只有 ${fmt(protein, 1)}g。下一餐可以补鸡蛋、鱼肉或豆制品。`;
    tone = 'ok';
  } else if (category === 'beverage') {
    title = sugar >= 20 ? '这杯糖分需要留意' : '这杯饮品负担较轻';
    copy = `约 ${fmt(calories, 0)} kcal，糖分 ${fmt(sugar, 1)}g。建议和今日总糖量一起看。`;
    tone = sugar >= 20 ? 'warn' : 'good';
  }

  const chips = [
    getMacroStatus('protein', protein).text === '很好' ? '蛋白充足' : `蛋白${getMacroStatus('protein', protein).text}`,
    `碳水${getMacroStatus('carbs', carbs).text}`,
    sodium > 450 ? `钠${getMacroStatus('sodium', sodium).text}` : sugar > 10 ? `糖分${getMacroStatus('sugar', sugar).text}` : `${selectedCount} 项可核对`,
  ];

  return {
    title,
    copy,
    tone,
    chips,
    dayCalPct: calPct,
    remainCalories: Math.max(0, DAY_TARGET.calories - calories),
    remainProtein: Math.max(0, DAY_TARGET.protein - protein),
  };
};

const getItemContribution = (nutrients: Record<string, any>) => {
  const scores = [
    { label: '热量主要来源', value: (Number(nutrients.calories) || 0) / 8 },
    { label: '蛋白主要来源', value: (Number(nutrients.protein) || 0) * 9 },
    { label: '碳水主要来源', value: (Number(nutrients.carbs) || 0) * 4 },
    { label: '脂肪主要来源', value: (Number(nutrients.fat) || 0) * 9 },
    { label: '纤维较好', value: (Number(nutrients.fiber) || 0) * 18 },
    { label: '钠需要留意', value: (Number(nutrients.sodium) || 0) / 12 },
  ].sort((a, b) => b.value - a.value);
  return scores[0]?.value > 0 ? scores[0].label : '建议核对份量';
};

type Props = {
  open: boolean;
  previewUrl: string | null;
  analysis: AiSnapAnalysis | null;
  mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  onChangeMealType: (v: Props['mealType']) => void;
  onClose: () => void;
  onSave: (payload: { mealType: Props['mealType']; items: { name: string; grams: number; nutrients: Record<string, number> }[] }) => Promise<void>;
  suggestions?: any;
  onOpenWiki: (itemName: string) => void;
};

export function AiSnapResultSheet(props: Props) {
  const { open, analysis } = props;
  const model = useAiSnapModel(analysis);

  const [screen, setScreen] = useState<'confirm' | 'details'>('confirm');
  const [saving, setSaving] = useState(false);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [obscuredBottom, setObscuredBottom] = useState(0);

  // reset when open/analysis changes
  useEffect(() => {
    if (!open) return;
    setScreen('confirm');
    setEditingIdx(null);
    model.resetFromAnalysis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, analysis?.food_name]);

  const selectedKcal = useMemo(() => model.totals.calories ?? 0, [model.totals]);
  const verdict = useMemo(() => getVerdict(model.totals as any, props.suggestions), [model.totals, props.suggestions]);
  const insight = useMemo(
    () => buildInsight(model.totals as any, analysis?.item_category, model.selectedCount),
    [model.totals, analysis?.item_category, model.selectedCount]
  );

  // dynamic bottom obscuring (Android WebView nav bar / browser UI)
  useEffect(() => {
    if (!open) return;

    const vv = (window as any).visualViewport as VisualViewport | undefined;
    const compute = () => {
      try {
        if (vv) {
          // layout viewport height (we use innerHeight as baseline)
          const layoutH = window.innerHeight || 0;
          const visualH = vv.height || 0;
          const offsetTop = vv.offsetTop || 0;
          const obscured = Math.max(0, layoutH - (visualH + offsetTop));
          setObscuredBottom(Math.round(obscured));
          return;
        }
      } catch {
        // ignore
      }
      setObscuredBottom(0);
    };

    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('orientationchange', compute);
    vv?.addEventListener('resize', compute);
    vv?.addEventListener('scroll', compute);

    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('orientationchange', compute);
      vv?.removeEventListener('resize', compute);
      vv?.removeEventListener('scroll', compute);
    };
  }, [open]);

  if (!open) return null;

  const disableSave = saving || model.selectedCount === 0;

  const node = (
    <div
      className="as-overlay"
      style={{ ['--as-obscured-bottom' as any]: `${obscuredBottom}px` }}
      onClick={(e) => e.target === e.currentTarget && props.onClose()}
    >
      <div className="as-sheet">
        <div className="as-handle" />
        <div className="as-top">
          {screen === 'details' ? (
            <button className="as-icon-btn" onClick={() => setScreen('confirm')} aria-label="返回">←</button>
          ) : (
            <button className="as-icon-btn" onClick={props.onClose} aria-label="关闭">✕</button>
          )}
          <div className="as-title">AI 快拍识别</div>
          <button className="as-icon-btn" onClick={() => setScreen(screen === 'confirm' ? 'details' : 'confirm')} aria-label="详情">
            {screen === 'confirm' ? '≡' : '✓'}
          </button>
        </div>

        <div className="as-content">
          {screen === 'confirm' ? (
            <>
              <div className="as-hero">
                <div className="as-photo">
                  {props.previewUrl ? <img src={props.previewUrl} alt="拍摄原图" /> : <div className="as-photo-empty">无预览</div>}
                </div>
              <div className="as-hero-meta">
                <div className="as-food-name">{analysis?.food_name || '识别结果'}</div>
                <div className="as-sub as-sub-row">
                  <div className={`as-stamp ${verdict.tone}`} aria-label={`结论：${verdict.text}`}>
                    <svg className="as-stamp-ring" viewBox="0 0 100 100" aria-hidden>
                      <circle cx="50" cy="50" r="44" fill="none" stroke="currentColor" strokeWidth="6" strokeDasharray="10 8" strokeLinecap="round" />
                    </svg>
                    <div className="as-stamp-text">{verdict.text}</div>
                  </div>
                  <span className="as-sub-text">识别到 {model.rawItems.length} 个菜品，已选 {model.selectedCount} 个</span>
                </div>
              </div>
            </div>

              <div className={`as-verdict-card ${insight.tone}`}>
                <div className="as-verdict-top">
                  <span>AI 结论</span>
                  <b>{verdict.text}</b>
                </div>
                <h3>{insight.title}</h3>
                <p>{insight.copy}</p>
                <div className="as-verdict-chips">
                  {insight.chips.map((chip) => <span key={chip}>{chip}</span>)}
                </div>
              </div>

              <div className="as-impact-grid">
                <div className="as-impact">
                  <span>日目标占比</span>
                  <b>{insight.dayCalPct}%</b>
                </div>
                <div className="as-impact">
                  <span>今日还可摄入</span>
                  <b>{fmt(insight.remainCalories, 0)} kcal</b>
                </div>
                <div className="as-impact">
                  <span>蛋白还差</span>
                  <b>{fmt(insight.remainProtein, 1)}g</b>
                </div>
              </div>

              <div className="as-quick-macros" aria-label="宏量营养概览">
                {[
                  ['calories', '热量', fmt(model.totals.calories, 0), 'kcal'],
                  ['protein', '蛋白', fmt(model.totals.protein, 1), 'g'],
                  ['carbs', '碳水', fmt(model.totals.carbs, 1), 'g'],
                  ['fat', '脂肪', fmt(model.totals.fat, 1), 'g'],
                ].map(([key, label, value, unit]) => {
                  const status = getMacroStatus(key, (model.totals as any)[key]);
                  return (
                    <div key={key} className="as-quick-macro">
                      <span>{label}</span>
                      <strong>{value}<small>{unit}</small></strong>
                      <em className={status.tone}>{status.text}</em>
                    </div>
                  );
                })}
              </div>

              <div className="as-list">
                {model.displayItems.map((it) => (
                  <div key={it.idx} className={`as-row ${!it.checked ? 'off' : ''}`}>
                    <label className="as-check">
                      <input
                        type="checkbox"
                        checked={it.checked}
                        onChange={() => {
                          model.setChecked(prev => {
                            const next = [...prev];
                            next[it.idx] = !next[it.idx];
                            return next;
                          });
                        }}
                      />
                    </label>
                    <button className="as-row-main" onClick={() => setEditingIdx(it.idx)}>
                      <div className="as-row-title">{it.name}</div>
                      <div className="as-row-sub">
                        {fmt(it.grams, 0)}g · {fmt(it.nutrients.calories, 0)}kcal
                      </div>
                      <div className="as-row-contribution">{getItemContribution(it.nutrients)}</div>
                    </button>
                    <button className="as-row-go" onClick={() => setEditingIdx(it.idx)} aria-label="编辑">›</button>
                  </div>
                ))}
              </div>

              <button className="as-more" onClick={() => setScreen('details')}>查看营养详情与建议</button>
            </>
          ) : (
            <>
              <div className="as-details-head">
                <div className="as-food-name">{analysis?.food_name || '识别结果'}</div>
                <div className="as-sub as-sub-row">
                  <div className={`as-stamp ${verdict.tone}`} aria-label={`结论：${verdict.text}`}>
                    <svg className="as-stamp-ring" viewBox="0 0 100 100" aria-hidden>
                      <circle cx="50" cy="50" r="44" fill="none" stroke="currentColor" strokeWidth="6" strokeDasharray="10 8" strokeLinecap="round" />
                    </svg>
                    <div className="as-stamp-text">{verdict.text}</div>
                  </div>
                  <span className="as-sub-text">已选 {model.selectedCount} 项，总热量 {fmt(selectedKcal, 0)} kcal</span>
                </div>
              </div>

              <div className="as-card">
                <div className="as-card-title">宏量营养（已按克重联动）</div>
                <div className="as-macro-grid">
                  {[
                    ['calories', '热量', fmt(model.totals.calories, 0), 'kcal'],
                    ['protein', '蛋白', fmt(model.totals.protein, 1), 'g'],
                    ['carbs', '碳水', fmt(model.totals.carbs, 1), 'g'],
                    ['fat', '脂肪', fmt(model.totals.fat, 1), 'g'],
                  ].map(([key, label, value, unit]) => {
                    const status = getMacroStatus(key, (model.totals as any)[key]);
                    return (
                      <div key={key} className="as-macro">
                        <span>{label}</span>
                        <b>{value}</b>
                        <i>{unit}</i>
                        <em className={status.tone}>{status.text}</em>
                      </div>
                    );
                  })}
                </div>
              </div>

              <details className="as-fold">
                <summary>微量营养（矿物质/维生素/其他）</summary>
                <div className="as-micro-grid">
                  {[
                    // minerals
                    ['sodium', '钠', 'mg'], ['potassium', '钾', 'mg'], ['calcium', '钙', 'mg'], ['iron', '铁', 'mg'],
                    ['zinc', '锌', 'mg'], ['magnesium', '镁', 'mg'], ['phosphorus', '磷', 'mg'], ['selenium', '硒', 'μg'],
                    // vitamins
                    ['vitamin_a', '维A', 'μg'], ['vitamin_b1', '维B1', 'mg'], ['vitamin_b2', '维B2', 'mg'], ['vitamin_b6', '维B6', 'mg'],
                    ['vitamin_b12', '维B12', 'μg'], ['vitamin_c', '维C', 'mg'], ['vitamin_d', '维D', 'μg'], ['vitamin_e', '维E', 'mg'],
                    ['vitamin_k', '维K', 'μg'], ['folate', '叶酸', 'μg'], ['niacin', '烟酸(B3)', 'mg'],
                    // others
                    ['cholesterol', '胆固醇', 'mg'], ['caffeine', '咖啡因', 'mg'], ['alcohol', '酒精', 'g'], ['water', '水', 'g'],
                  ].map(([k, label, unit]) => (
                    <div key={k as string} className="as-micro">
                      <span>{label as string}</span>
                      <b>{fmt((model.totals as any)[k as string], 1)}</b>
                      <i>{unit as string}</i>
                    </div>
                  ))}
                </div>
              </details>

              <div className="as-card">
                <div className="as-card-title">建议</div>
                {props.suggestions?.suggestions?.length ? (
                  <>
                    {/* 首条突出 */}
                    <div className="as-sug-main">
                      <div className={`as-sug-tag ${props.suggestions.suggestions[0].type || 'tip'}`}>{props.suggestions.suggestions[0].type || 'tip'}</div>
                      <div className="as-sug-body">
                        <div className="as-sug-title">{props.suggestions.suggestions[0].title}</div>
                        <div className="as-sug-detail">{props.suggestions.suggestions[0].detail}</div>
                      </div>
                    </div>
                    {props.suggestions.suggestions.length > 1 && (
                      <details className="as-fold" style={{ marginTop: 10 }}>
                        <summary>更多建议（{props.suggestions.suggestions.length - 1}）</summary>
                        <div style={{ padding: '0 12px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {props.suggestions.suggestions.slice(1).map((s: any, i: number) => (
                            <div key={i} className="as-sug-row">
                              <div className={`as-sug-dot ${s.type || 'tip'}`} />
                              <div style={{ flex: 1 }}>
                                <div className="as-sug-title">{s.title}</div>
                                <div className="as-sug-detail">{s.detail}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </>
                ) : (
                  <div className="as-tip">暂无建议</div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="as-bottom">
          <div className="as-bottom-left">
            <div className="as-bottom-kcal">{fmt(selectedKcal, 0)} kcal</div>
            <div className="as-bottom-sub">已选 {model.selectedCount} 项</div>
          </div>
          <div className="as-bottom-mid">
            <select value={props.mealType} onChange={(e) => props.onChangeMealType(e.target.value as any)}>
              <option value="breakfast">早餐</option>
              <option value="lunch">午餐</option>
              <option value="dinner">晚餐</option>
              <option value="snack">加餐</option>
            </select>
          </div>
          <button
            className="as-save"
            disabled={disableSave}
            onClick={async () => {
              setSaving(true);
              try {
                await props.onSave({
                  mealType: props.mealType,
                  items: model.selectedItems.map(it => ({ name: it.name, grams: it.grams, nutrients: it.nutrients })),
                });
              } finally {
                setSaving(false);
              }
            }}
          >
            {saving ? '保存中…' : `保存 ${model.selectedCount} 项`}
          </button>
        </div>

        <AiSnapItemEditorSheet
          open={editingIdx != null}
          itemIdx={editingIdx ?? 0}
          analysis={analysis}
          name={editingIdx != null ? model.displayItems[editingIdx]?.name : ''}
          grams={editingIdx != null ? model.displayItems[editingIdx]?.grams : 100}
          nutrients={editingIdx != null ? model.displayItems[editingIdx]?.nutrients : {}}
          onClose={() => setEditingIdx(null)}
          onChange={(next) => {
            if (editingIdx == null) return;
            model.setEdits(prev => {
              const arr = [...prev];
              const cur = arr[editingIdx] || { name: model.displayItems[editingIdx].name, grams: model.displayItems[editingIdx].grams };
              arr[editingIdx] = {
                name: next.name != null ? next.name : cur.name,
                grams: next.grams != null ? next.grams : cur.grams,
              };
              return arr;
            });
          }}
          onOpenWiki={(itemName) => props.onOpenWiki(itemName)}
        />
      </div>

      <style>{css}</style>
    </div>
  );

  // Escape the workspace stacking context while inheriting the active web skin.
  return typeof document !== 'undefined' ? createPortal(node, webPortalTarget()) : node;
}

const css = `
.as-overlay{
  position: fixed; inset: 0;
  z-index: 1500;
  background: rgba(2,6,23,0.78);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  display:flex;
  align-items:flex-end;
  justify-content:center;
}
.as-sheet{
  width: 100%;
  max-width: 720px;
  height: calc(var(--app-height, 100vh) - var(--safe-area-inset-top, 0px));
  background: rgba(15,23,42,0.98);
  border-top-left-radius: 22px;
  border-top-right-radius: 22px;
  border: 1px solid rgba(148,163,184,0.16);
  box-shadow: 0 20px 60px rgba(0,0,0,0.6);
  display:flex;
  flex-direction:column;
  overflow:hidden;
  animation: asIn 220ms cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes asIn{from{transform:translateY(24px);opacity:0.6}to{transform:translateY(0);opacity:1}}
.as-editor{height: min(520px, calc(var(--app-height, 100vh) - 24px)); border-radius:22px; margin-bottom: 10px;}
.as-handle{width:44px;height:5px;border-radius:4px;background:rgba(148,163,184,0.25);margin:10px auto 6px;}
.as-top{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:8px 14px;border-bottom:1px solid rgba(148,163,184,0.1)}
.as-title{font-weight:900;color:rgba(226,232,240,0.95)}
.as-icon-btn{width:36px;height:36px;border-radius:10px;border:1px solid rgba(148,163,184,0.14);background:rgba(30,41,59,0.7);color:rgba(226,232,240,0.95);cursor:pointer;font-size:16px}
.as-content{flex:1;overflow:auto;-webkit-overflow-scrolling:touch;padding:12px 14px calc(150px + var(--as-obscured-bottom, 0px));}
/* 动态预留底部遮挡高度（安卓系统导航栏/浏览器UI） */
.as-bottom{position: sticky; bottom: 0; padding: 10px 14px calc(10px + var(--safe-area-inset-bottom, 0px) + var(--as-obscured-bottom, 0px));
  background: linear-gradient(180deg, rgba(15,23,42,0.0), rgba(15,23,42,0.92) 30%, rgba(15,23,42,0.98));
  border-top: 1px solid rgba(148,163,184,0.12);
  display:flex; gap:10px; align-items:center;
}
.as-bottom-left{min-width:110px}
.as-bottom-kcal{font-weight:950;color:#38bdf8;font-size:18px;line-height:1}
.as-bottom-sub{font-size:11px;color:rgba(148,163,184,0.7);margin-top:4px}
.as-bottom-mid select{background:rgba(2,6,23,0.35);border:1px solid rgba(148,163,184,0.14);color:rgba(226,232,240,0.95);border-radius:12px;padding:10px 10px;font-weight:800}
.as-save{flex:1;border-radius:14px;padding:12px 12px;font-weight:950;border:1px solid rgba(56,189,248,0.35);background:linear-gradient(135deg,#0369a1,#38bdf8);color:#0b1220;cursor:pointer}
.as-save:disabled{opacity:0.55;cursor:not-allowed}
.as-hero{display:flex;gap:12px;align-items:center}
.as-photo{width:86px;height:110px;border-radius:16px;overflow:hidden;border:1px solid rgba(148,163,184,0.14);background:rgba(2,6,23,0.35);flex-shrink:0}
.as-photo img{width:100%;height:100%;object-fit:cover}
.as-photo-empty{padding:12px;color:rgba(148,163,184,0.7);font-size:12px}
.as-hero-meta{min-width:0}
.as-food-name{font-weight:950;color:rgba(226,232,240,0.95);font-size:16px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.as-sub{margin-top:4px;color:rgba(148,163,184,0.7);font-size:12px}
.as-sub-row{display:flex;align-items:center;gap:10px;flex-wrap:wrap}
.as-sub-text{color:rgba(148,163,184,0.7);font-size:12px}
.as-verdict-card{
  position:relative;
  overflow:hidden;
  margin-top:12px;
  padding:14px;
  border-radius:18px;
  border:1px solid rgba(34,197,94,0.22);
  background:
    radial-gradient(circle at 100% 0%, rgba(34,197,94,0.16), transparent 42%),
    linear-gradient(135deg, rgba(15,23,42,0.88), rgba(20,83,45,0.28));
}
.as-verdict-card.ok{
  border-color:rgba(245,158,11,0.24);
  background:
    radial-gradient(circle at 100% 0%, rgba(245,158,11,0.14), transparent 42%),
    linear-gradient(135deg, rgba(15,23,42,0.88), rgba(120,53,15,0.24));
}
.as-verdict-card.warn,.as-verdict-card.bad{
  border-color:rgba(244,63,94,0.28);
  background:
    radial-gradient(circle at 100% 0%, rgba(244,63,94,0.16), transparent 42%),
    linear-gradient(135deg, rgba(15,23,42,0.9), rgba(76,29,49,0.34));
}
.as-verdict-card:after{
  content:'';
  position:absolute;
  right:-22px;
  top:-26px;
  width:88px;
  height:88px;
  border-radius:999px;
  border:1px solid rgba(255,255,255,0.08);
  background:rgba(255,255,255,0.035);
  pointer-events:none;
}
.as-verdict-top{position:relative;z-index:1;display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:8px}
.as-verdict-top span{color:rgba(203,213,225,0.76);font-size:11px;font-weight:900;letter-spacing:0.08em;text-transform:uppercase}
.as-verdict-top b{display:inline-flex;align-items:center;min-height:24px;padding:0 9px;border-radius:999px;background:rgba(15,23,42,0.58);border:1px solid rgba(255,255,255,0.08);color:#86efac;font-size:11px;font-weight:950;white-space:nowrap}
.as-verdict-card.ok .as-verdict-top b{color:#fbbf24}
.as-verdict-card.warn .as-verdict-top b,.as-verdict-card.bad .as-verdict-top b{color:#fb7185}
.as-verdict-card h3{position:relative;z-index:1;margin:0;color:rgba(248,250,252,0.96);font-size:18px;line-height:1.2;font-weight:950}
.as-verdict-card p{position:relative;z-index:1;margin:8px 0 0;color:rgba(203,213,225,0.78);font-size:13px;line-height:1.55}
.as-verdict-chips{position:relative;z-index:1;display:flex;flex-wrap:wrap;gap:7px;margin-top:12px}
.as-verdict-chips span{display:inline-flex;align-items:center;min-height:26px;padding:0 10px;border-radius:999px;background:rgba(15,23,42,0.58);border:1px solid rgba(148,163,184,0.13);color:rgba(226,232,240,0.88);font-size:11px;font-weight:850}
.as-impact-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;margin-top:10px}
.as-impact{min-width:0;padding:10px;border-radius:14px;background:rgba(15,23,42,0.58);border:1px solid rgba(148,163,184,0.11)}
.as-impact span{display:block;color:rgba(148,163,184,0.66);font-size:10px;font-weight:850;margin-bottom:4px}
.as-impact b{display:block;color:rgba(241,245,249,0.96);font-size:14px;line-height:1.15;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.as-quick-macros{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:9px;margin-top:10px}
.as-quick-macro{padding:10px;border-radius:14px;background:rgba(2,6,23,0.35);border:1px solid rgba(148,163,184,0.12)}
.as-quick-macro span{display:block;color:rgba(148,163,184,0.75);font-size:11px;font-weight:850}
.as-quick-macro strong{display:flex;align-items:baseline;gap:4px;color:#38bdf8;font-size:17px;font-weight:950;line-height:1;margin-top:5px}
.as-quick-macro small{font-size:10px;color:rgba(148,163,184,0.7);font-weight:850}
.as-quick-macro em,.as-macro em{display:inline-flex;align-items:center;min-height:20px;padding:0 7px;margin-top:7px;border-radius:999px;font-style:normal;font-size:10px;font-weight:900;background:rgba(148,163,184,0.1);color:rgba(203,213,225,0.78)}
.as-quick-macro em.good,.as-macro em.good{color:#86efac;background:rgba(16,185,129,0.12)}
.as-quick-macro em.ok,.as-macro em.ok{color:#fbbf24;background:rgba(245,158,11,0.12)}
.as-quick-macro em.warn,.as-macro em.warn{color:#fb7185;background:rgba(244,63,94,0.12)}

/* Stamp verdict */
.as-stamp{
  position:relative;
  width: 92px;
  height: 92px;
  flex-shrink:0;
  display:grid;
  place-items:center;
  transform: rotate(-10deg);
  filter: drop-shadow(0 6px 18px rgba(0,0,0,0.35));
}
.as-stamp-ring{position:absolute;inset:0;width:100%;height:100%;opacity:0.92}
.as-stamp-text{
  font-weight: 1000;
  font-size: 18px;
  letter-spacing: 1px;
  text-transform:none;
  text-align:center;
  line-height:1.05;
  color: currentColor;
  text-shadow: 0 1px 0 rgba(0,0,0,0.35);
}
.as-stamp.good{color:#22c55e}
.as-stamp.ok{color:#38bdf8}
.as-stamp.warn{color:#ef4444}
.as-stamp.bad{color:#b91c1c}
.as-list{margin-top:12px;display:flex;flex-direction:column;gap:8px}
.as-row{display:flex;gap:10px;align-items:center;border:1px solid rgba(148,163,184,0.12);border-radius:16px;background:rgba(2,6,23,0.32);padding:10px 10px}
.as-row.off{opacity:0.45}
.as-check input{width:18px;height:18px;accent-color:#8b5cf6}
.as-row-main{flex:1;min-width:0;border:none;background:transparent;color:inherit;text-align:left;cursor:pointer}
.as-row-title{font-weight:900;font-size:13px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.as-row-sub{margin-top:2px;font-size:12px;color:rgba(148,163,184,0.8)}
.as-row-contribution{margin-top:3px;font-size:12px;color:rgba(226,232,240,0.76);font-weight:850;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.as-row-go{width:32px;height:32px;border-radius:12px;border:1px solid rgba(148,163,184,0.12);background:rgba(15,23,42,0.5);color:rgba(226,232,240,0.9);cursor:pointer;font-size:18px}
.as-more{margin-top:12px;width:100%;border-radius:14px;padding:12px;border:1px solid rgba(148,163,184,0.14);background:rgba(15,23,42,0.55);color:rgba(226,232,240,0.92);font-weight:900;cursor:pointer}
.as-card{margin-top:12px;border-radius:16px;border:1px solid rgba(148,163,184,0.12);background:rgba(15,23,42,0.5);padding:12px}
.as-card-title{font-weight:900;color:rgba(226,232,240,0.92);font-size:13px;margin-bottom:10px}
.as-macro-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}
.as-macro{border:1px solid rgba(148,163,184,0.12);border-radius:14px;background:rgba(2,6,23,0.35);padding:10px}
.as-macro span{display:block;font-size:11px;color:rgba(148,163,184,0.75);font-weight:800}
.as-macro b{display:block;font-size:16px;color:#38bdf8;font-weight:950;margin-top:4px;line-height:1}
.as-macro i{font-style:normal;font-size:11px;color:rgba(148,163,184,0.7)}
.as-fold{margin-top:12px;border:1px solid rgba(148,163,184,0.12);border-radius:16px;background:rgba(15,23,42,0.5);overflow:hidden}
.as-fold summary{padding:12px;font-weight:900;color:rgba(226,232,240,0.92);cursor:pointer;list-style:none}
.as-fold summary::-webkit-details-marker{display:none}
.as-micro-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;padding:0 12px 12px}
.as-micro{border:1px solid rgba(148,163,184,0.12);border-radius:14px;background:rgba(2,6,23,0.35);padding:10px}
.as-micro span{display:block;font-size:11px;color:rgba(148,163,184,0.75);font-weight:800}
.as-micro b{display:block;font-size:13px;color:rgba(226,232,240,0.95);font-weight:950;margin-top:4px}
.as-micro i{font-style:normal;font-size:10px;color:rgba(148,163,184,0.7)}
.as-tip{font-size:12px;color:rgba(148,163,184,0.7)}
.as-sug-main{display:flex;gap:10px;align-items:flex-start;padding:10px;border:1px solid rgba(148,163,184,0.12);border-radius:14px;background:rgba(2,6,23,0.35)}
.as-sug-tag{min-width:54px;text-transform:uppercase;font-size:11px;font-weight:950;padding:6px 8px;border-radius:999px;text-align:center;border:1px solid rgba(148,163,184,0.14);background:rgba(15,23,42,0.55);color:rgba(226,232,240,0.9)}
.as-sug-tag.warning{border-color:rgba(244,63,94,0.4);background:rgba(244,63,94,0.12);color:#fb7185}
.as-sug-tag.positive{border-color:rgba(16,185,129,0.4);background:rgba(16,185,129,0.12);color:#34d399}
.as-sug-tag.tip{border-color:rgba(56,189,248,0.4);background:rgba(56,189,248,0.12);color:#38bdf8}
.as-sug-title{font-weight:950;color:rgba(226,232,240,0.95);font-size:13px}
.as-sug-detail{margin-top:4px;color:rgba(148,163,184,0.85);font-size:12px;line-height:1.5;white-space:pre-wrap}
.as-sug-row{display:flex;gap:10px;align-items:flex-start}
.as-sug-dot{width:10px;height:10px;border-radius:999px;margin-top:4px;background:rgba(148,163,184,0.4)}
.as-sug-dot.warning{background:#fb7185}
.as-sug-dot.positive{background:#34d399}
.as-sug-dot.tip{background:#38bdf8}
.as-field{margin-top:10px}
.as-label{font-size:12px;color:rgba(148,163,184,0.75);font-weight:900;margin-bottom:6px}
.as-input{width:100%;background:rgba(2,6,23,0.35);border:1px solid rgba(148,163,184,0.14);border-radius:14px;padding:12px;color:rgba(226,232,240,0.95);font-weight:900}
.as-stepper{display:flex;align-items:center;gap:10px}
.as-step{width:40px;height:40px;border-radius:14px;border:1px solid rgba(148,163,184,0.14);background:rgba(15,23,42,0.55);color:rgba(226,232,240,0.95);font-weight:950;font-size:18px;cursor:pointer}
.as-step-val{flex:1;text-align:center;font-weight:950;color:rgba(226,232,240,0.95)}
.as-quick{display:flex;flex-wrap:wrap;gap:8px;margin-top:10px}
.as-chip{padding:8px 10px;border-radius:999px;border:1px solid rgba(148,163,184,0.14);background:rgba(15,23,42,0.55);color:rgba(226,232,240,0.9);font-weight:900;cursor:pointer}
.as-chip.active{border-color:rgba(56,189,248,0.5);background:rgba(56,189,248,0.12);color:#38bdf8}
`;
